/**
 * Dashboard feature grid component
 * Apple/Tesla aesthetic: glass cards, staggered animations, category colors
 */

import { Link } from 'react-router-dom';
import { useDashboardStore } from '@saiens/shared/stores';
import { ChevronUp, ChevronDown, Check } from 'lucide-react';
import { cn } from '@saiens/shared/utils';
import {
  FEATURE_BLOCKS,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  type FeatureBlock,
  type FeatureCategory,
} from '@/config/features';

export function FeatureGrid() {
  const { visibleBlockIds, blockOrder, isEditMode } = useDashboardStore();

  if (isEditMode) {
    return <EditModeGrid />;
  }

  const sortedBlocks = [...blockOrder]
    .filter((id) => visibleBlockIds.includes(id))
    .map((id) => FEATURE_BLOCKS.find((b) => b.id === id))
    .filter((b): b is FeatureBlock => b !== undefined);

  const categoryOrder: FeatureCategory[] = ['core', 'tools', 'management', 'hr', 'system'];
  const grouped = new Map<FeatureCategory, FeatureBlock[]>();
  for (const block of sortedBlocks) {
    const cat = block.category;
    if (!grouped.has(cat)) {
      grouped.set(cat, []);
    }
    grouped.get(cat)!.push(block);
  }

  let globalIndex = 0;

  return (
    <div>
      {categoryOrder.map((cat) => {
        const blocks = grouped.get(cat);
        if (!blocks || blocks.length === 0) return null;

        return (
          <div key={cat} className="mb-6">
            <p className="text-caption mb-3 text-muted-foreground">
              {CATEGORY_LABELS[cat]}
            </p>
            <div className="grid grid-cols-3 gap-3">
              {blocks.map((block) => {
                const Icon = block.icon;
                const staggerClass = `stagger-${++globalIndex}`;
                const iconColor = CATEGORY_COLORS[block.category];

                if (!block.route) {
                  return (
                    <div
                      key={block.id}
                      className={cn(
                        'animate-fade-in-up flex flex-col items-center rounded-2xl border border-black/[0.04] bg-white/60 px-2 py-5 opacity-40',
                        staggerClass,
                      )}
                    >
                      <div className="mb-2.5 flex h-11 w-11 items-center justify-center rounded-2xl bg-black/[0.03] text-muted-foreground">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="text-[11px] font-medium text-muted-foreground">{block.label}</span>
                    </div>
                  );
                }

                return (
                  <Link
                    key={block.id}
                    to={block.route}
                    className={cn(
                      'animate-fade-in-up press-effect flex flex-col items-center rounded-2xl border border-black/[0.04] bg-white/80 px-2 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
                      staggerClass,
                    )}
                  >
                    <div className={cn('mb-2.5 flex h-11 w-11 items-center justify-center rounded-2xl bg-black/[0.03]', iconColor)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-medium text-foreground">{block.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EditModeGrid() {
  const { visibleBlockIds, blockOrder, toggleBlock, moveBlock } =
    useDashboardStore();

  const sortedBlocks = blockOrder
    .map((id) => FEATURE_BLOCKS.find((b) => b.id === id))
    .filter((b): b is FeatureBlock => b !== undefined);

  return (
    <div className="space-y-2">
      {sortedBlocks.map((block, index) => {
        const Icon = block.icon;
        const isVisible = visibleBlockIds.includes(block.id);

        return (
          <div
            key={block.id}
            className={cn(
              'flex items-center gap-3 rounded-2xl p-3 transition-all duration-200',
              isVisible
                ? 'border border-black/[0.04] bg-white/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
                : 'bg-black/[0.02] opacity-40',
            )}
          >
            <button
              type="button"
              onClick={() => toggleBlock(block.id)}
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-all duration-200',
                isVisible
                  ? 'bg-primary text-white shadow-[0_1px_3px_rgba(0,113,227,0.3)]'
                  : 'border-2 border-black/10 bg-white',
              )}
            >
              {isVisible && <Check className="h-3.5 w-3.5" />}
            </button>

            <div className={cn(
              'flex h-9 w-9 items-center justify-center rounded-xl',
              isVisible ? 'bg-black/[0.03]' : 'bg-black/[0.02]',
              isVisible ? CATEGORY_COLORS[block.category] : 'text-muted-foreground',
            )}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className={cn(
                'text-sm font-medium',
                isVisible ? 'text-foreground' : 'text-muted-foreground',
              )}>
                {block.label}
              </p>
              <p className="text-[11px] text-muted-foreground">{block.description}</p>
            </div>

            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => moveBlock(index, index - 1)}
                disabled={index === 0}
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground disabled:opacity-20"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => moveBlock(index, index + 1)}
                disabled={index === sortedBlocks.length - 1}
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground disabled:opacity-20"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
